import { runSessionStatus } from './session-status';
import { sessionStatusLockName } from './session-status';
import type { LockRequestFn } from './session-status';
import type { SessionValidity } from './session-status';
import type { OrganizationSelection } from './types';

export const sessionChannelName = 'sgs-session-sync';

export type SessionSyncMessage =
  | { type: 'logout' }
  | { type: 'login' }
  | { type: 'theme-change'; value: string }
  | ({ type: 'org-switch' } & OrganizationSelection);

export type CrossTabChannel = {
  post: (message: SessionSyncMessage) => void;
  close: () => void;
};

export type ChannelFactory = (
  channelName: string,
  onMessage: (message: SessionSyncMessage) => void
) => CrossTabChannel | null;

type CrossTabBroadcasterDeps = {
  channelName: string;
  createChannel: ChannelFactory;
  onLogout?: () => void;
  onLogin?: () => void;
  onThemeChange?: (value: string) => void;
  onOrgSwitch?: (selection: OrganizationSelection) => void;
};

export type CrossTabBroadcaster = {
  broadcastLogout: () => void;
  broadcastLogin: () => void;
  broadcastTheme: (value: string) => void;
  broadcastOrgSwitch: (selection: OrganizationSelection) => void;
  close: () => void;
  isOpen: () => boolean;
};

export function createCrossTabBroadcaster(deps: CrossTabBroadcasterDeps): CrossTabBroadcaster {
  function dispatch(message: SessionSyncMessage): void {
    if (message.type === 'logout') {
      if (deps.onLogout) {
        deps.onLogout();
      }
    } else if (message.type === 'login') {
      if (deps.onLogin) {
        deps.onLogin();
      }
    } else if (message.type === 'theme-change') {
      if (deps.onThemeChange) {
        deps.onThemeChange(message.value);
      }
    } else if (message.type === 'org-switch') {
      if (deps.onOrgSwitch) {
        deps.onOrgSwitch({
          name: message.name,
          precedence: message.precedence,
        });
      }
    }
  }

  const channel = deps.createChannel(deps.channelName, dispatch);

  function post(message: SessionSyncMessage): void {
    if (channel) {
      channel.post(message);
    }
  }

  function broadcastLogout(): void {
    post({ type: 'logout' });
  }

  function broadcastLogin(): void {
    post({ type: 'login' });
  }

  function broadcastTheme(value: string): void {
    post({ type: 'theme-change', value: value });
  }

  function broadcastOrgSwitch(selection: OrganizationSelection): void {
    post({ type: 'org-switch', name: selection.name, precedence: selection.precedence });
  }

  function close(): void {
    if (channel) {
      channel.close();
    }
  }

  function isOpen(): boolean {
    return channel !== null;
  }

  return {
    broadcastLogout: broadcastLogout,
    broadcastLogin: broadcastLogin,
    broadcastTheme: broadcastTheme,
    broadcastOrgSwitch: broadcastOrgSwitch,
    close: close,
    isOpen: isOpen,
  };
}

export type SessionCoreDeps = {
  channelName: string;
  lockName?: string;
  intervalMs: number;
  lockRequest: LockRequestFn | null;
  createChannel: ChannelFactory;
  validate: () => Promise<SessionValidity>;
  onLogout: () => void;
  onOrgSwitch: (selection: OrganizationSelection) => void;
  initialObservedOrg?: string;
  setInterval: (callback: () => void, ms: number) => number;
  clearInterval: (handle: number) => void;
};

export type SessionCore = {
  start: () => void;
  stop: () => void;
  isLeader: () => boolean;
  broadcastLogout: () => void;
  broadcastOrgSwitch: (selection: OrganizationSelection) => void;
};

export function createSessionCore(deps: SessionCoreDeps): SessionCore {
  let leader = false;
  let stopSessionStatus: (() => void) | null = null;

  const broadcaster = createCrossTabBroadcaster({
    channelName: deps.channelName,
    createChannel: deps.createChannel,
    onLogout: () => {
      deps.onLogout();
    },
    onOrgSwitch: (selection) => {
      deps.onOrgSwitch(selection);
    },
  });

  function start(): void {
    if (stopSessionStatus === null) {
      stopSessionStatus = runSessionStatus({
        intervalMs: deps.intervalMs,
        lockName: deps.lockName ?? sessionStatusLockName,
        lockRequest: deps.lockRequest,
        validate: deps.validate,
        initialObservedOrg: deps.initialObservedOrg,
        onUnauthenticated: () => {
          leader = true;
          broadcaster.broadcastLogout();
          deps.onLogout();
        },
        onOrgChange: (selection, switched) => {
          leader = true;
          if (switched) {
            broadcaster.broadcastOrgSwitch(selection);
          }
          deps.onOrgSwitch(selection);
        },
        setInterval: deps.setInterval,
        clearInterval: deps.clearInterval,
      });
    }
  }

  function stop(): void {
    if (stopSessionStatus !== null) {
      stopSessionStatus();
      stopSessionStatus = null;
    }
    broadcaster.close();
    leader = false;
  }

  function isLeader(): boolean {
    return leader;
  }

  function broadcastLogout(): void {
    broadcaster.broadcastLogout();
  }

  function broadcastOrgSwitch(selection: OrganizationSelection): void {
    broadcaster.broadcastOrgSwitch(selection);
  }

  return {
    start: start,
    stop: stop,
    isLeader: isLeader,
    broadcastLogout: broadcastLogout,
    broadcastOrgSwitch: broadcastOrgSwitch,
  };
}
